import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import { ui, uiHandlers } from "../uiStyles";

function money(x) {
  const n = Number(x || 0);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function statusStyle(status) {
  const s = String(status || "DRAFT").toUpperCase();

  if (s === "CONVERTED") {
    return { color: "#f5c542", borderColor: "rgba(245,197,66,0.45)" };
  }

  if (s === "APPROVED") {
    return { color: "#86efac", borderColor: "rgba(134,239,172,0.35)" };
  }

  if (s === "SENT") {
    return { color: "#93c5fd", borderColor: "rgba(147,197,253,0.35)" };
  }

  if (s === "REJECTED") {
    return { color: "#fca5a5", borderColor: "rgba(252,165,165,0.35)" };
  }

  return { color: "#dbe7f3", borderColor: "rgba(255,255,255,0.12)" };
}

export default function Estimates() {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showNewEstimateSetup, setShowNewEstimateSetup] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  async function load() {
    setLoading(true);
    setErr("");

    try {
      const [estimateData, clientData, archivedClients, jobData] = await Promise.all([
        apiRequest(showArchived ? "/estimates?archived=true" : "/estimates"),
        apiRequest("/clients"),
        apiRequest("/clients?archived=true").catch(() => []),
        apiRequest("/jobs/"),
      ]);

      setEstimates(Array.isArray(estimateData) ? estimateData : []);
      setClients([
        ...(Array.isArray(clientData) ? clientData : []),
        ...(Array.isArray(archivedClients) ? archivedClients : []),
      ]);
      setJobs(Array.isArray(jobData) ? jobData : []);
    } catch (e) {
      setErr(String(e?.message || e || "Failed to load estimates."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [showArchived]);

  const filteredJobs = selectedClientId
    ? jobs.filter((job) => String(job.client_id) === String(selectedClientId))
    : jobs;

  function continueToNewEstimate() {
    if (!selectedClientId) {
      setErr("Select a client before creating an estimate.");
      return;
    }

    if (!selectedJobId) {
      setErr("Select a job before creating an estimate.");
      return;
    }

    navigate(`/estimates/new?job_id=${selectedJobId}`);
  }

  async function setEstimateArchived(event, id, next) {
    event.preventDefault();
    event.stopPropagation();
    setArchivingId(id);
    setErr("");
    try {
      await apiRequest(`/estimates/${id}/archive`, {
        method: "PATCH",
        body: { archived: next },
      });
      setEstimates((current) => current.filter((row) => row.id !== id));
    } catch (e) {
      setErr(String(e?.message || e || "Could not update this estimate."));
    } finally {
      setArchivingId(null);
    }
  }
  async function removeEstimate(id) {
    const ok = window.confirm("Delete this estimate?");
    if (!ok) return;

    try {
      await apiRequest(`/estimates/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Delete failed."));
    }
  }

  const visibleEstimates = estimates.filter((est) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    const clientName = est.client?.company_name || est.client?.contact_name || "";
    return (
      (est.estimate_number || "").toLowerCase().includes(query) ||
      clientName.toLowerCase().includes(query)
    );
  });

  return (
    <div style={ui.page}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: "#ffffff", fontSize: 24 }}>
          Estimates
        </h2>
        <p style={{ marginTop: 8, color: "#9fb0c3", fontSize: 14 }}>
          Review pending, approved, and converted estimates.
        </p>
      </div>

      <div style={ui.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "#ffffff", fontSize: 20 }}>
              Estimate Workspace
            </h3>
            <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 6 }}>
              {loading ? "Loading estimates…" : `${visibleEstimates.length} estimates found`}
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setShowNewEstimateSetup((v) => !v);
              setErr("");
            }}
            style={ui.btnPrimary}
            {...uiHandlers.primaryHover}
          >
            {showNewEstimateSetup ? "Hide New Estimate" : "+ New Estimate"}
          </button>
        </div>

        {showNewEstimateSetup && (
          <div
            style={{
              ...ui.subCard,
              marginBottom: 16,
              border: "1px solid rgba(0,194,255,0.28)",
              background:
                "linear-gradient(135deg, rgba(0,194,255,0.10), rgba(16,38,58,0.96))",
            }}
          >
            <div
              style={{
                color: "#00c2ff",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 6,
              }}
            >
              Estimate Setup
            </div>

            <h3 style={{ marginTop: 0, marginBottom: 8, color: "#ffffff" }}>
              Select a client and job before creating an estimate.
            </h3>

            <p style={{ color: "#c9d8e8", marginTop: 0, marginBottom: 16, lineHeight: 1.55 }}>
              Estimates must be connected to a JOB so the estimate, invoice, payment, and receipt history stays organized under the correct work file.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: "#dbe7f3", fontSize: 13, fontWeight: 700 }}>
                  Client
                </span>
                <select
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                    setSelectedJobId("");
                  }}
                  style={ui.input}
                >
                  <option value="">Select Client</option>
                  {clients.filter((client) => !client.archived).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name || client.contact_name || `Client #${client.id}`}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: "#dbe7f3", fontSize: 13, fontWeight: 700 }}>
                  Job
                </span>
                <select
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  style={ui.input}
                  disabled={!selectedClientId}
                >
                  <option value="">
                    {selectedClientId ? "Select Job" : "Select a client first"}
                  </option>
                  {filteredJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {`JOB ID #${job.id} — ${job.defendant_name || job.court_name || job.job_number || "Untitled Job"}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={continueToNewEstimate}
                style={ui.btnPrimary}
                {...uiHandlers.primaryHover}
              >
                Continue to Estimate
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowNewEstimateSetup(false);
                  setSelectedClientId("");
                  setSelectedJobId("");
                  setErr("");
                }}
                style={ui.btnSecondary}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search estimate # or client"
            style={{ ...ui.input, flex: "1 1 220px" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#dbe7f3" }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
            />
            Archived
          </label>
        </div>

        {err && (
          <div
            style={{
              ...ui.subCard,
              color: "#fecaca",
              border: "1px solid rgba(239,68,68,0.35)",
              background: "rgba(127,29,29,0.28)",
              marginBottom: 16,
            }}
          >
            {err}
          </div>
        )}

        {loading ? (
          <div style={{ color: "#9fb0c3" }}>Loading…</div>
        ) : visibleEstimates.length === 0 ? (
          <div style={{ ...ui.subCard, color: "#9fb0c3" }}>
            No estimates found.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 14,
            }}
          >
            {visibleEstimates.map((est) => {
              const status = String(est.status || "DRAFT").toUpperCase();

              return (
                <div
                  key={est.id}
                  onClick={() => navigate(`/estimates/${est.id}`)}
                  style={{
                    ...ui.subCard,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      marginBottom: 14,
                    }}
                  >
                    <div>
                      <div style={{ color: "#ffffff", fontWeight: 800, fontSize: 17 }}>
                        {est.estimate_number || `EST-${est.id}`}
                      </div>
                      <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 5 }}>
                        {est.client?.company_name ||
                          est.client?.contact_name ||
                          clients.find((client) => String(client.id) === String(est.client_id))?.company_name ||
                          clients.find((client) => String(client.id) === String(est.client_id))?.contact_name ||
                          `Client #${est.client_id}`}
                      </div>
                    </div>

                    <span
                      style={{
                        border: "1px solid",
                        borderRadius: 999,
                        padding: "5px 9px",
                        fontSize: 12,
                        fontWeight: 800,
                        height: "fit-content",
                        ...statusStyle(status),
                      }}
                    >
                      {status}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 10,
                      marginBottom: 14,
                    }}
                  >
                    <Mini label="Issue Date" value={fmtDate(est.issue_date)} />
                    <Mini label="Due Date" value={fmtDate(est.due_date)} />
                    <Mini label="Total" value={money(est.total)} gold />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      borderTop: "1px solid rgba(255,255,255,0.06)",
                      paddingTop: 14,
                    }}
                  >
                    <Link
                      to={`/estimates/${est.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={ui.btnPrimary}
                      {...uiHandlers.primaryHover}
                    >
                      Open Estimate
                    </Link>

                    <button
                      type="button"
                      disabled={archivingId === est.id}
                      onClick={(e) => setEstimateArchived(e, est.id, !showArchived)}
                      style={{
                        ...ui.btnSecondary,
                        borderColor: "rgba(239,68,68,0.55)",
                        color: "#fca5a5",
                        boxShadow: "none",
                      }}
                    >
                      {archivingId === est.id ? "Saving…" : showArchived ? "Unarchive" : "Archive"}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEstimate(est.id);
                      }}
                      style={{
                        ...ui.btnSecondary,
                        borderColor: "rgba(239,68,68,0.55)",
                        color: "#fca5a5",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 12,
        padding: 12,
        background: "#0b2239",
        minWidth: 0,
      }}
    >
      <div
        style={{
          color: "#9fb0c3",
          fontSize: 11,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: gold ? "#f5c542" : "#ffffff",
          fontWeight: 800,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </div>
    </div>
  );
}
